import React, { useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { novedadFotoPublicUrl } from "../../api/Novedades/NovedadesApi";
import { htmlToPlainText } from "../../utils/novedadHtml";
import {
  groupNovedadesByTag,
  itemsForNovedadTag,
  novedadTagColor,
  novedadTagLabels,
  NUEVOS_TAG_KEY,
  rememberNuevosIds,
} from "../../utils/novedadesFeedUtils";
import { NovedadHtmlBody } from "./NovedadRichText";

function itemAccent(item, fallbackKey) {
  const label = novedadTagLabels(item)[0] || fallbackKey || "General";
  return novedadTagColor(label);
}

function coloredChipSx(color, selected) {
  return {
    height: 28,
    fontWeight: 800,
    borderRadius: "999px",
    border: "1px solid",
    bgcolor: selected ? color : alpha(color, 0.14),
    color: selected ? "#fff" : color,
    borderColor: selected ? color : alpha(color, 0.38),
    "& .MuiChip-label": { px: 1.1 },
    "&:hover": {
      bgcolor: selected ? color : alpha(color, 0.24),
    },
  };
}

function formatFecha(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-ES");
}

function TagCard({ title, count, date, nuevoCount, accent, onClick }) {
  const theme = useTheme();
  const unread = nuevoCount > 0;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        p: 1.5,
        minHeight: 96,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        position: "relative",
        border: unread ? "2px solid" : "1px solid",
        borderColor: unread ? "#00b894" : alpha(accent, 0.28),
        background: `linear-gradient(165deg, ${alpha(
          unread ? "#00b894" : accent,
          unread ? 0.2 : 0.14,
        )} 0%, ${alpha(theme.palette.background.paper, 0.96)} 58%)`,
        boxShadow: unread ? `0 8px 20px ${alpha("#00b894", 0.22)}` : "none",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 22px ${alpha(unread ? "#00b894" : accent, 0.22)}`,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography fontWeight={800} sx={{ lineHeight: 1.25, color: accent }}>
          {title}
        </Typography>
        {unread ? (
          <Chip
            size="small"
            label={nuevoCount === 1 ? "1 sin leer" : `${nuevoCount} sin leer`}
            sx={{
              height: 22,
              fontWeight: 800,
              bgcolor: "#ff3b30",
              color: "white",
            }}
          />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
        {count} {count === 1 ? "novedad" : "novedades"}
        {date ? ` · ${date}` : ""}
      </Typography>
    </Box>
  );
}

function NovedadItemCard({ item, accent, onOpenPhoto, onMarkRead }) {
  const [expanded, setExpanded] = useState(false);
  const preview = htmlToPlainText(item.Descripcion);
  const color = accent || itemAccent(item);
  const tags = novedadTagLabels(item);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, next) => {
        setExpanded(next);
        if (next && item.nuevo) onMarkRead?.(item.Id);
      }}
      TransitionProps={{
        onEntered: (node) => {
          node?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
        },
      }}
      sx={{
        borderRadius: "12px !important",
        border: item.nuevo ? "2px solid" : "1px solid",
        borderColor: item.nuevo ? "#00b894" : alpha(color, 0.22),
        bgcolor: alpha(
          item.nuevo ? "#00b894" : color,
          item.nuevo ? 0.12 : 0.05,
        ),
        overflow: "hidden",
        boxShadow: item.nuevo
          ? `inset 4px 0 0 #00b894, 0 6px 16px ${alpha("#00b894", 0.18)}`
          : `inset 4px 0 0 ${color}`,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color }} />}
        sx={{
          alignItems: "flex-start",
          "& .MuiAccordionSummary-content": {
            minWidth: 0,
            my: 1,
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            mt: 0.75,
          },
        }}
      >
        <Box sx={{ pr: 1, minWidth: 0, width: "100%" }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
          >
            {tags.map((tag) => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                sx={{
                  ...coloredChipSx(novedadTagColor(tag), true),
                  height: 22,
                  fontSize: "0.7rem",
                }}
              />
            ))}
            {item.Version ? (
              <Chip
                size="small"
                label={`v${item.Version}`}
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  bgcolor: alpha(color, 0.12),
                  color,
                  border: "1px solid",
                  borderColor: alpha(color, 0.28),
                }}
              />
            ) : null}
            {item.nuevo ? (
              <Chip
                size="small"
                label="SIN LEER"
                sx={{
                  height: 22,
                  fontWeight: 800,
                  bgcolor: "#ff3b30",
                  color: "white",
                }}
              />
            ) : null}
          </Stack>
          <Typography fontWeight={800} sx={{ lineHeight: 1.3, mt: 0.6 }}>
            {item.Titulo}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatFecha(item.Fecha)}
          </Typography>
          {!expanded && preview ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                whiteSpace: "pre-line",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {preview}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
        <Box
          sx={{
            pt: 1.5,
            borderTop: "1px solid",
            borderColor: alpha(color, 0.18),
          }}
        >
          <NovedadHtmlBody html={item.Descripcion} />
          {item.fotos?.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1.5,
              }}
            >
              {item.fotos.map((f) => (
                <Box
                  key={f.Id || f.Archivo}
                  component="img"
                  src={novedadFotoPublicUrl(f.Archivo)}
                  alt=""
                  onClick={() =>
                    onOpenPhoto({
                      src: novedadFotoPublicUrl(f.Archivo),
                      alt: `${item.Titulo} (v${item.Version})`,
                    })
                  }
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 220,
                    borderRadius: 1,
                    border: "1px solid #eee",
                    objectFit: "contain",
                    cursor: "zoom-in",
                    "&:hover": { opacity: 0.9 },
                  }}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function NovedadesFeed({
  items,
  preferNuevos,
  onOpenPhoto,
  onMarkRead,
}) {
  const theme = useTheme();
  const [selectedTag, setSelectedTag] = useState(
    preferNuevos ? NUEVOS_TAG_KEY : null,
  );

  const groups = useMemo(() => groupNovedadesByTag(items), [items]);
  const nuevoCount = useMemo(
    () => (items || []).filter((item) => item.nuevo).length,
    [items],
  );
  const keepNuevosIdsRef = useRef(new Set());
  const visibleItems = useMemo(() => {
    rememberNuevosIds(items, keepNuevosIdsRef.current);
    return selectedTag
      ? itemsForNovedadTag(items, selectedTag, keepNuevosIdsRef.current)
      : [];
  }, [items, selectedTag]);
  const selectedGroup = groups.find((g) => g.key === selectedTag);
  const selectedTitle =
    selectedTag === NUEVOS_TAG_KEY
      ? "Nuevos"
      : selectedGroup?.label || "Novedades";

  const selectedColor = novedadTagColor(selectedTag || selectedTitle);

  return (
    <Box>
      {selectedTag ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <IconButton
              size="small"
              onClick={() => setSelectedTag(null)}
              aria-label="Volver a áreas"
              sx={{
                bgcolor: alpha(selectedColor, 0.12),
                color: selectedColor,
                "&:hover": { bgcolor: alpha(selectedColor, 0.22) },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box
              sx={{
                width: 10,
                height: 36,
                borderRadius: 8,
                bgcolor: selectedColor,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900} sx={{ color: selectedColor }}>
                {selectedTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {visibleItems.length}{" "}
                {visibleItems.length === 1 ? "novedad" : "novedades"}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.85}
            sx={{
              overflowX: "auto",
              pb: 0.75,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: 8,
              },
            }}
          >
            {nuevoCount > 0 ? (
              <Chip
                size="small"
                label={`Nuevos (${nuevoCount})`}
                onClick={() => setSelectedTag(NUEVOS_TAG_KEY)}
                sx={coloredChipSx(
                  novedadTagColor(NUEVOS_TAG_KEY),
                  selectedTag === NUEVOS_TAG_KEY,
                )}
              />
            ) : null}
            {groups.map((group) => (
              <Chip
                key={group.key}
                size="small"
                label={`${group.label} (${group.items.length})`}
                onClick={() => setSelectedTag(group.key)}
                sx={coloredChipSx(
                  novedadTagColor(group.key),
                  selectedTag === group.key,
                )}
              />
            ))}
          </Stack>

          <Stack spacing={1}>
            {visibleItems.map((item) => (
              <NovedadItemCard
                key={`${selectedTag}-${item.Id}`}
                item={item}
                accent={itemAccent(item, selectedTag)}
                onOpenPhoto={onOpenPhoto}
                onMarkRead={onMarkRead}
              />
            ))}
          </Stack>
        </Stack>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1.25,
          }}
        >
          {nuevoCount > 0 ? (
            <TagCard
              title="Nuevos"
              count={nuevoCount}
              date=""
              nuevoCount={nuevoCount}
              accent={novedadTagColor(NUEVOS_TAG_KEY)}
              onClick={() => setSelectedTag(NUEVOS_TAG_KEY)}
            />
          ) : null}
          {groups.map((group) => (
            <TagCard
              key={group.key}
              title={group.label}
              count={group.items.length}
              date={formatFecha(group.latestFecha)}
              nuevoCount={group.nuevoCount}
              accent={novedadTagColor(group.key)}
              onClick={() => setSelectedTag(group.key)}
            />
          ))}
          {!groups.length ? (
            <Typography color="text.secondary">No hay novedades.</Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export function NovedadesDialogTitle() {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <CampaignOutlinedIcon color="primary" />
      <Box>
        <Typography
          variant="overline"
          sx={{ fontWeight: 800, letterSpacing: "0.08em" }}
        >
          Novedades
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          Last changes
        </Typography>
      </Box>
    </Stack>
  );
}
